"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useAlbums } from "@/lib/album-context";
import { useToast } from "@/components/toast-provider";
import * as s from "@/lib/styles";

interface Props {
  open: boolean;
  onClose: () => void;
}

/** Create a new album owned by the signed-in user. */
export default function CreateAlbumModal({ open, onClose }: Props) {
  const { showToast } = useToast();
  const { createAlbum, selectAlbum } = useAlbums();
  const router = useRouter();

  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open) setName("");
  }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !creating) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, creating, onClose]);

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setCreating(true);
    try {
      const album = await createAlbum(trimmed);
      await selectAlbum(album.id);
      showToast("success", `Created: ${album.name}`);
      onClose();
      router.push("/photos");
      router.refresh();
    } catch (e) {
      showToast("error", e instanceof Error ? e.message : "Create failed");
    } finally {
      setCreating(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className={s.modalOverlay + " z-50 p-4"}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-album-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !creating) onClose();
      }}
    >
      <div className={s.modalContent}>
        <div className="flex items-center justify-between gap-3">
          <h2 id="create-album-title" className="text-base font-semibold text-purple m-0">
            New album
          </h2>
        </div>

        <input
          autoFocus
          className={s.input + " w-full"}
          placeholder="Album name"
          value={name}
          disabled={creating}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void submit();
          }}
        />

        <div className="flex justify-end gap-2">
          <button
            type="button"
            className={s.btnAuxiliary}
            onClick={onClose}
            disabled={creating}
          >
            Cancel
          </button>
          <button
            type="button"
            className={s.btnPrimary}
            onClick={() => void submit()}
            disabled={creating || !name.trim()}
          >
            {creating ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
